import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import {
  rootReducer,
  DATAGRID_INITIAL_PAGE,
  DATAGRID_INITIAL_PAGE_SIZE,
} from '@verantos-dev/core-annotation-tool';

import { BATCH_PROGRESS_TAB_OPTIONS } from '../constants';

export interface MetricsState {
  selectedProjectId: string | null;
  selectedAnnotatorId: string | null;
  selectedBatchwiseProgressTab: string;
  batchWiseStartDate: string | null;
  batchWiseEndDate: string | null;
  summaryBatchTypeId: string | null;
  summaryStartDate: string | null;
  summaryEndDate: string | null;
  encounterStatsBatchTypeId: string | null;
  encounterStatsStartDate: string | null;
  encounterStatsEndDate: string | null;
  irrFilterTypeId: string | null;
  irrScoreBatchTypeId: string | null;
  irrScoreDate: string | null;
  irrCardFilters: {
    annotator1: string[] | null;
    annotator2: string[] | null;
    conceptIds: string[] | null;
    entityIds: string[] | null;
  } | null;
  summaryTablePage: number;
  summaryTablePageSize: number;
  encounterStatsTablePage: number;
  encounterStatsTablePageSize: number;
  irrScoreTablePage: number;
  irrScoreTablePageSize: number;
  batchProgressTablePage: number;
  batchProgressTablePageSize: number;
  holidaysTablePage: number;
  holidaysTablePageSize: number;
}

const initialState: MetricsState = {
  selectedProjectId: null,
  selectedAnnotatorId: null,
  selectedBatchwiseProgressTab: BATCH_PROGRESS_TAB_OPTIONS[0],
  batchWiseStartDate: null,
  batchWiseEndDate: null,
  summaryBatchTypeId: null,
  summaryStartDate: null,
  summaryEndDate: null,
  encounterStatsBatchTypeId: null,
  encounterStatsStartDate: null,
  encounterStatsEndDate: null,
  irrFilterTypeId: null,
  irrScoreDate: null,
  irrScoreBatchTypeId: null,
  irrCardFilters: null,
  summaryTablePage: DATAGRID_INITIAL_PAGE,
  summaryTablePageSize: DATAGRID_INITIAL_PAGE_SIZE,
  encounterStatsTablePage: DATAGRID_INITIAL_PAGE,
  encounterStatsTablePageSize: DATAGRID_INITIAL_PAGE_SIZE,
  irrScoreTablePage: DATAGRID_INITIAL_PAGE,
  irrScoreTablePageSize: DATAGRID_INITIAL_PAGE_SIZE,
  batchProgressTablePage: DATAGRID_INITIAL_PAGE,
  batchProgressTablePageSize: DATAGRID_INITIAL_PAGE_SIZE,
  holidaysTablePage: DATAGRID_INITIAL_PAGE,
  holidaysTablePageSize: DATAGRID_INITIAL_PAGE_SIZE,
};

const metricsSlice = createSlice({
  name: 'metrics',
  initialState,
  reducers: {
    setSelectedProjectId: (state, action: PayloadAction<string>) => {
      state.selectedProjectId = action.payload;
    },
    clearSelectedProjectId: (state) => {
      state.selectedProjectId = null;
    },
    setSelectedAnnotatorId: (state, action: PayloadAction<string>) => {
      state.selectedAnnotatorId = action.payload;
    },
    clearSelectedAnnotatorId: (state) => {
      state.selectedAnnotatorId = null;
    },
    setSelectedBatchwiseProgressTab: (state, action: PayloadAction<string>) => {
      state.selectedBatchwiseProgressTab = action.payload;
    },
    setBatchwiseStartDate: (state, action: PayloadAction<string>) => {
      state.batchWiseStartDate = action.payload;
    },
    setBatchwiseEndDate: (state, action: PayloadAction<string>) => {
      state.batchWiseEndDate = action.payload;
    },
    setSummaryBatchTypeId: (state, action: PayloadAction<string>) => {
      state.summaryBatchTypeId = action.payload;
    },
    clearSummaryBatchTypeId: (state) => {
      state.summaryBatchTypeId = null;
    },
    setSummaryStartDate: (state, action: PayloadAction<string>) => {
      state.summaryStartDate = action.payload;
    },
    clearSummaryStartDate: (state) => {
      state.summaryStartDate = null;
    },
    setSummaryEndDate: (state, action: PayloadAction<string>) => {
      state.summaryEndDate = action.payload;
    },
    clearSummaryEndDate: (state) => {
      state.summaryEndDate = null;
    },
    setEncounterStatsBatchTypeId: (state, action: PayloadAction<string>) => {
      state.encounterStatsBatchTypeId = action.payload;
    },
    clearEncounterStatsBatchTypeId: (state) => {
      state.encounterStatsBatchTypeId = null;
    },
    setEncounterStatsStartDate: (state, action: PayloadAction<string>) => {
      state.encounterStatsStartDate = action.payload;
    },
    clearEncounterStatsStartDate: (state) => {
      state.encounterStatsStartDate = null;
    },
    setEncounterStatsEndDate: (state, action: PayloadAction<string>) => {
      state.encounterStatsEndDate = action.payload;
    },
    clearEncounterStatsEndDate: (state) => {
      state.encounterStatsEndDate = null;
    },
    setIRRFilterTypeId: (state, action: PayloadAction<string>) => {
      state.irrFilterTypeId = action.payload;
    },
    clearIRRFilterTypeId: (state) => {
      state.irrFilterTypeId = null;
    },
    setIRRScoreDate: (state, action: PayloadAction<string>) => {
      state.irrScoreDate = action.payload;
    },
    clearIRRScoreDate: (state) => {
      state.irrScoreDate = null;
    },
    setIRRScoreBatchTypeId: (state, action: PayloadAction<string>) => {
      state.irrScoreBatchTypeId = action.payload;
    },
    clearIRRScoreBatchTypeId: (state) => {
      state.irrScoreBatchTypeId = null;
    },
    setIRRCardFilters: (state, action: PayloadAction<MetricsState['irrCardFilters']>) => {
      state.irrCardFilters = action.payload;
    },
    clearIRRCardFilters: (state) => {
      state.irrCardFilters = null;
    },
    setSummaryTablePaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.summaryTablePage = action.payload.page;
      state.summaryTablePageSize = action.payload.pageSize;
    },
    setEncounterStatsTablePaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.encounterStatsTablePage = action.payload.page;
      state.encounterStatsTablePageSize = action.payload.pageSize;
    },
    setIRRScoreTablePaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.irrScoreTablePage = action.payload.page;
      state.irrScoreTablePageSize = action.payload.pageSize;
    },
    setBatchProgressTablePaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.batchProgressTablePage = action.payload.page;
      state.batchProgressTablePageSize = action.payload.pageSize;
    },
    setHolidaysTablePaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.holidaysTablePage = action.payload.page;
      state.holidaysTablePageSize = action.payload.pageSize;
    },
    resetAllStates: (state) => {
      state.selectedAnnotatorId = null;
      state.selectedBatchwiseProgressTab = BATCH_PROGRESS_TAB_OPTIONS[0];
      state.batchWiseStartDate = null;
      state.batchWiseEndDate = null;
      state.summaryBatchTypeId = null;
      state.summaryStartDate = null;
      state.summaryEndDate = null;
      state.encounterStatsBatchTypeId = null;
      state.encounterStatsStartDate = null;
      state.encounterStatsEndDate = null;
      state.irrFilterTypeId = null;
      state.irrScoreDate = null;
      state.irrScoreBatchTypeId = null;
      state.irrCardFilters = null;
      state.summaryTablePage = DATAGRID_INITIAL_PAGE;
      state.summaryTablePageSize = DATAGRID_INITIAL_PAGE_SIZE;
      state.encounterStatsTablePage = DATAGRID_INITIAL_PAGE;
      state.encounterStatsTablePageSize = DATAGRID_INITIAL_PAGE_SIZE;
      state.irrScoreTablePage = DATAGRID_INITIAL_PAGE;
      state.irrScoreTablePageSize = DATAGRID_INITIAL_PAGE_SIZE;
      state.batchProgressTablePage = DATAGRID_INITIAL_PAGE;
      state.batchProgressTablePageSize = DATAGRID_INITIAL_PAGE_SIZE;
      state.holidaysTablePage = DATAGRID_INITIAL_PAGE;
      state.holidaysTablePageSize = DATAGRID_INITIAL_PAGE_SIZE;
    },
  },
});

export const {
  setSelectedProjectId,
  clearSelectedProjectId,
  setSelectedAnnotatorId,
  clearSelectedAnnotatorId,
  setSelectedBatchwiseProgressTab,
  setBatchwiseStartDate,
  setBatchwiseEndDate,
  setSummaryBatchTypeId,
  clearSummaryBatchTypeId,
  setSummaryStartDate,
  clearSummaryStartDate,
  setSummaryEndDate,
  clearSummaryEndDate,
  setEncounterStatsBatchTypeId,
  clearEncounterStatsBatchTypeId,
  setEncounterStatsStartDate,
  clearEncounterStatsStartDate,
  setEncounterStatsEndDate,
  clearEncounterStatsEndDate,
  setIRRFilterTypeId,
  clearIRRFilterTypeId,
  setIRRScoreDate,
  clearIRRScoreDate,
  setIRRScoreBatchTypeId,
  clearIRRScoreBatchTypeId,
  setIRRCardFilters,
  clearIRRCardFilters,
  resetAllStates,
  setSummaryTablePaginationModel,
  setEncounterStatsTablePaginationModel,
  setIRRScoreTablePaginationModel,
  setBatchProgressTablePaginationModel,
  setHolidaysTablePaginationModel,
} = metricsSlice.actions;

export { metricsSlice };

declare module '@verantos-dev/core-annotation-tool' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof metricsSlice> {}
}

metricsSlice.injectInto(rootReducer);
